import React, { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPeopleBehind as fetchPeopleBehindData,
  createPeopleBehind,
  updatePeopleBehind,
  deletePeopleBehind,
} from "../../redux/slice/peopleBehind.js";
import { Delete as DeleteIcon, Edit } from "@mui/icons-material";
import { SlideshowLightbox } from "lightbox.js-react";

const PeopleBehind = () => {
  const dispatch = useDispatch();
  const peopleBehindData = useSelector((state) => state.peopleBehind) || {};
  const { peopleBehind, status, error } = peopleBehindData;
  const [open, setOpen] = useState(false);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    post: "",
    location: "",
    images: [],
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    dispatch(fetchPeopleBehindData());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "An unknown error occurred";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [error]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleOpen = (person = null) => {
    setCurrentPerson(person);
    if (person) {
      setFormData({
        name: person.name,
        post: person.post,
        location: person.location,
        images: [],
      });
      setImagePreviews(person.images.map((img) => img.url));
    } else {
      setFormData({ name: "", post: "", location: "", images: [] });
      setImagePreviews([]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentPerson(null);
    setImagePreviews([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, images: files }));
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = new FormData();
      dataToSubmit.append("name", formData.name);
      dataToSubmit.append("post", formData.post);
      dataToSubmit.append("location", formData.location);
      formData.images.forEach((image) => {
        dataToSubmit.append("images", image);
      });

      if (currentPerson) {
        console.log("dataToSubmit:", dataToSubmit);

        await dispatch(
          updatePeopleBehind({
            id: currentPerson._id,
            peopleBehindData: {
              name: formData.name,
              post: formData.post,
              location: formData.location,
              images: formData.images, // array of File objects
            },
          })
        ).unwrap();
        setSnackbarMessage("Person updated successfully!");
        setSnackbarSeverity("success");
      } else {
        await dispatch(
          createPeopleBehind({
            name: formData.name,
            post: formData.post,
            location: formData.location,
            images: formData.images,
          })
        ).unwrap();
        setSnackbarMessage("Person added successfully!");
        setSnackbarSeverity("success");
      }
      setSnackbarOpen(true);
      handleClose();
      dispatch(fetchPeopleBehindData());
    } catch (err) {
      const errorMessage = err.message || "Failed to save person";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = (id) => {
    setPersonToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (personToDelete) {
      try {
        await dispatch(deletePeopleBehind(personToDelete)).unwrap();
        setSnackbarMessage("Person deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (err) {
        const errorMessage = err.message || "Failed to delete person";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
      setDeleteDialogOpen(false);
      setPersonToDelete(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedPeople = peopleBehind?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (status === "loading" || showLoader)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress sx={{ color: "#F68633" }} />
      </Box>
    );

  if (error && !peopleBehind)
    return (
      <Alert severity="error">
        Error:{" "}
        {typeof error === "string"
          ? error
          : error?.message || "Failed to load data"}
      </Alert>
    );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        People Behind
      </Typography>
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#e0752d",
          "&:hover": { backgroundColor: "#F68633" },
          textTransform: "none",
          mb: 2,
        }}
        onClick={() => handleOpen()}
      >
        Add Person
      </Button>

      {paginatedPeople && paginatedPeople.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Post</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPeople.map((person) => (
                <TableRow key={person._id}>
                  <TableCell>{person.name}</TableCell>
                  <TableCell>{person.post}</TableCell>
                  <TableCell>{person.location}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {(person.images || []).map((image, index) => (
                        <SlideshowLightbox key={index}>
                          <img
                            src={`${process.env.REACT_APP_BASE_URL_ASSETS}${image.url}`}
                            alt={person.name}
                            style={{ width: "100px", height: "auto" }}
                          />
                        </SlideshowLightbox>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => handleOpen(person)}
                    >
                      <Edit />
                    </Button>
                    <Button
                      color="secondary"
                      size="small"
                      sx={{ marginLeft: 1 }}
                      onClick={() => handleDelete(person._id)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">No People Found.</Alert>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={peopleBehind?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Modal for Adding/Editing Person */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {currentPerson ? "Edit Person" : "Add Person"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Post"
              name="post"
              value={formData.post}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
            <input
              type="file"
              multiple
              onChange={handleImageChange}
              accept="image/*"
              style={{ marginTop: 16 }}
            />
            <Stack direction="row" spacing={1} sx={{ marginTop: 2 }}>
              {imagePreviews.map((preview, index) => (
                <Avatar
                  key={index}
                  src={preview}
                  alt={`preview-${index}`}
                  sx={{ width: 40, height: 40 }}
                />
              ))}
            </Stack>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                backgroundColor: "#e0752d",
                "&:hover": { backgroundColor: "#F68633" },
                textTransform: "none",
                mt: 2,
              }}
            >
              {currentPerson ? "Update" : "Create"}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Confirmation Dialog for Deletion */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this person? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PeopleBehind;
